import type { ICart, ICartEntryOffer, ICartEntryReward } from './incentive-cart';
import {
  ICartEntryType,
  addToCart,
  filterCartEntries,
  findCartEntry,
  isCartEntryEqual,
  updateCartEntry,
} from './incentive-cart';
import { isCartEntryQuantityValid, isStackableOffer } from './utils';

/** Legacy: allow multiple reward lines to merge by quantity. */
export type AddInRestaurantRewardPolicy =
  | { kind: 'unlimited' }
  /** Total reward applications capped (same semantics as `rewardRedemptionLimit`). */
  | { kind: 'capped'; maxApplications: number };

export type AddInRestaurantRewardResult = {
  cart: ICart;
  /** Reward lines *fully removed* to stay under the cap; Redux must `removeAppliedReward` for each. */
  evictedRewards: ICartEntryReward[];
  /** Reward lines whose quantity was reduced by 1 (partial eviction); Redux must `unApplyReward`
   * once per entry so stagedCartPoints and timesApplied stay in sync. */
  decrementedRewards: ICartEntryReward[];
};

/**
 * @param policyOrLegacyAllowStacking - Prefer {@link AddInRestaurantRewardPolicy}. Passing `true`
 * means unlimited stacking; `false` means single reward (replace all reward lines).
 */
export const addReward = (
  cart: ICart,
  newCartEntry: ICartEntryReward,
  policyOrLegacyAllowStacking: AddInRestaurantRewardPolicy | boolean
): AddInRestaurantRewardResult => {
  const policy: AddInRestaurantRewardPolicy =
    typeof policyOrLegacyAllowStacking === 'boolean'
      ? policyOrLegacyAllowStacking
        ? { kind: 'unlimited' }
        : { kind: 'capped', maxApplications: 1 }
      : policyOrLegacyAllowStacking;

  if (policy.kind === 'unlimited') {
    let cartResult = cart;

    const existingCartEntry = findCartEntry(cart, newCartEntry);

    if (existingCartEntry) {
      const newQuantity = existingCartEntry.quantity + 1;

      if (isCartEntryQuantityValid(newQuantity)) {
        cartResult = updateCartEntry(cart, existingCartEntry, { quantity: newQuantity });
      }
    } else {
      cartResult = addToCart(cart, newCartEntry);
    }

    return { cart: cartResult, evictedRewards: [], decrementedRewards: [] };
  }

  const maxApplications = Math.max(1, policy.maxApplications);

  const existingCartEntry = findCartEntry(cart, newCartEntry);
  const rewardEntries = cart.filter(cartEntry => cartEntry.type === ICartEntryType.REWARD);
  const totalRewardQuantity = rewardEntries.reduce((sum, e) => sum + (e.quantity ?? 0), 0);

  if (existingCartEntry) {
    if (totalRewardQuantity < maxApplications) {
      const newQuantity = existingCartEntry.quantity + 1;
      if (isCartEntryQuantityValid(newQuantity)) {
        return {
          cart: updateCartEntry(cart, existingCartEntry, { quantity: newQuantity }),
          evictedRewards: [],
          decrementedRewards: [],
        };
      }
    }
    // Total reward capacity reached (or per-entry max hit) — fall through to evict oldest
  }

  if (totalRewardQuantity >= maxApplications) {
    const toRemove = rewardEntries[0] as ICartEntryReward;

    // Adding the same reward as the one that would be evicted is a no-op: decrement + increment
    // of the same line cancels out, and would also cause a spurious removeAppliedReward dispatch.
    if (isCartEntryEqual(toRemove, newCartEntry)) {
      return { cart, evictedRewards: [], decrementedRewards: [] };
    }

    // Prefer decrementing over full removal: if the oldest line has more than one application,
    // just remove one slot so the remaining applications stay in the cart.
    const isFullyEvicted = toRemove.quantity <= 1;
    const evictedCart = isFullyEvicted
      ? filterCartEntries(cart, ce => !isCartEntryEqual(ce, toRemove))
      : updateCartEntry(cart, toRemove, { quantity: toRemove.quantity - 1 });

    // Add the new reward to the updated cart: increment if the same type is already present
    // (possible after a partial decrement), otherwise append a new entry.
    const existingAfterEviction = findCartEntry(evictedCart, newCartEntry);
    const resultCart = existingAfterEviction
      ? isCartEntryQuantityValid(existingAfterEviction.quantity + 1)
        ? updateCartEntry(evictedCart, existingAfterEviction, {
            quantity: existingAfterEviction.quantity + 1,
          })
        : evictedCart
      : addToCart(evictedCart, newCartEntry);

    // Only report the entry as evicted when it was fully removed; partial decrements keep the
    // line alive so Redux should not dispatch removeAppliedReward for it.
    return {
      cart: resultCart,
      evictedRewards: isFullyEvicted ? [toRemove] : [],
      decrementedRewards: isFullyEvicted ? [] : [toRemove],
    };
  }

  return { cart: addToCart(cart, newCartEntry), evictedRewards: [], decrementedRewards: [] };
};

export const addOffer = (cart: ICart, newCartEntry: ICartEntryOffer) => {
  // for stackable offer try to remove it from the list otherwise,
  // remove all no stackable from the list
  const filterPredicate: Parameters<typeof filterCartEntries>[1] = isStackableOffer(newCartEntry)
    ? ce => !isCartEntryEqual(ce, newCartEntry)
    : ce => ce.type !== ICartEntryType.OFFER || isStackableOffer(ce);

  const filteredCart = filterCartEntries(cart, filterPredicate);

  return addToCart(filteredCart, newCartEntry);
};
